import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { Form, Col, Select, Input, DatePicker } from 'antd';

import { filterTypes } from '../../constants/filterType';

import { renderNoDataOrSpinner } from '../../utils';

import './styles.less';

const { RangePicker } = DatePicker;

export const Filter = ({
  rootContainer,
  filterType,
  name,
  label,
  span,
  data,
  dataLoading,
  dataSearchAction,
  mode,
  keyPropertyName,
  valuePropertyName,
  titlePropertyName,
  textPropertyName,
  filterByChildData,
  placeholder,
  wrapperCol,
  maxTagCount,
  subscribeDataChangingAndFetchNewData,
  rules,
  fetchImmediately,
  onChange,
  disabled,
  inputType,
  tooltip,
}) => {
  const [fieldTimeout, setFieldTimeout] = useState();
  const [searchFieldValue, setSearchFieldValue] = useState('');
  const [canFetchData, setCanFetchData] = useState(false);
  const [subscriptionOnDropdownOpen, setSubscriptionOnDropdownOpen] = useState();

  const dispatch = useDispatch();

  useEffect(() => {
    onMount();
    return onUnmount;
  }, []);

  useEffect(() => fetchData(), [subscribeDataChangingAndFetchNewData, canFetchData]);

  /*
   * Subscribe for Dropdown Open;
   * Fetch data only when dropdown has opened;
   */ // ----------------------------------------------------------------------
  const onMount = () => {
    if (typeof subscriptionOnDropdownOpen !== 'function') {
      setSubscriptionOnDropdownOpen(() => onDropdownVisibleChange);
    }

    fetchData();
  };

  /*
   * Unsubscribe search timeout and dropdown open;
   */ // ----------------------------------------------------------------------
  const onUnmount = () => {
    if (fieldTimeout) {
      clearTimeout(fieldTimeout);
      setFieldTimeout(undefined);
    }

    if (typeof subscriptionOnDropdownOpen === 'function') {
      setSubscriptionOnDropdownOpen();
    }

    if (canFetchData) {
      setCanFetchData(false);
    }
  };

  /*
   * Fetch data only when dropdown has opened;
   */ // ----------------------------------------------------------------------
  const fetchData = () => {
    if (typeof dataSearchAction === 'function' && (canFetchData || fetchImmediately)) {
      dispatch(dataSearchAction());
    }
  };

  const handleSearch = (searchString) => {
    if (typeof dataSearchAction === 'function') {
      if (fieldTimeout) {
        clearTimeout(fieldTimeout);
        setFieldTimeout(undefined);
      }

      setSearchFieldValue(searchString);

      const timeout = setTimeout(() => dispatch(dataSearchAction(searchString)), 500);
      setFieldTimeout(timeout);
    }
  };

  const getMaxTagPlaceholder = (omittedValues) => {
    if (omittedValues && omittedValues.length > 0) {
      return `+${omittedValues.length} Items`;
    }
  };

  const onDropdownVisibleChange = (open) => {
    if (!canFetchData) {
      setCanFetchData(true);
    }
  };

  /*
   * Create Dropdown Yes/No;
   *
   * Simple Dropdown with selection yes or no;
   *
   * If @placeholder will be empty then value will be from label;
   */ // ----------------------------------------------------------------------
  const createDropdownYesNo = () => {
    return (
      <Form.Item name={[name]} label={label} className="filter-form-item" wrapperCol={wrapperCol} rules={rules} tooltip={tooltip}>
        <Select
          allowClear
          showArrow
          filterOption={false}
          getPopupContainer={rootContainer}
          placeholder={placeholder ?? `Select ${label}`}
          onChange={onChange}
          disabled={disabled}
        >
          <Select.Option key="1" value={1} title="Empty">
            Empty
          </Select.Option>
          <Select.Option key="2" value={2} title="No">
            No
          </Select.Option>
          <Select.Option key="3" value={3} title="Yes">
            Yes
          </Select.Option>
        </Select>
      </Form.Item>
    );
  };

  /*
   * Create Input;
   *
   * Simple Input allow you enter text;
   *
   * @placeholder - Placeholder of select.
   * If @placeholder will be empty then value will be from label;
   */ // ----------------------------------------------------------------------
  const createInput = () => {
    return (
      <Form.Item name={[name]} label={label} className="filter-form-item" wrapperCol={wrapperCol} rules={rules} tooltip={tooltip}>
        <Input type={inputType ?? 'text'} placeholder={placeholder ?? `Enter ${label}`} onChange={onChange} disabled={disabled} />
      </Form.Item>
    );
  };

  /*
   * Create DropdownWithLocalData;
   *
   * Dropdown where data is predefined/static data.
   * Searching will not call server method and will filter by children data;
   *
   * If @placeholder will be empty then value will be from label;
   * @mode - Set mode of Select. Can be 'single', 'tags', 'multiple'. Multiple is default value;
   * @maxTagCount - Max tag text length to show. Default value is 4;
   *
   * @data should have key, value and text properties;
   * @key - key of Select.Option
   * @value - Default to filter with this property
   * @text - title of Select after select this Option and title in dropdown
   */ // ----------------------------------------------------------------------
  const createDropdownWithLocalData = () => {
    const key = keyPropertyName ? keyPropertyName : 'key';
    const value = valuePropertyName ? valuePropertyName : key;
    const text = textPropertyName ? textPropertyName : 'value';
    const title = titlePropertyName ? titlePropertyName : text;

    return (
      <Form.Item name={[name]} label={label} className="filter-form-item" wrapperCol={wrapperCol} rules={rules} tooltip={tooltip}>
        <Select
          allowClear
          showSearch
          showArrow
          mode={mode ? mode : 'multiple'}
          placeholder={placeholder ?? `Select ${label}`}
          getPopupContainer={rootContainer}
          optionFilterProp="children"
          filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
          maxTagCount={maxTagCount ?? 4}
          maxTagPlaceholder={getMaxTagPlaceholder}
          onChange={onChange}
          disabled={disabled}
          notFoundContent={renderNoDataOrSpinner(false)}
          loading={dataLoading}
        >
          {data && data.length
            ? data.map((d) => (
                <Select.Option key={d[key]} value={d[value]} title={d[title]}>
                  {d[text]}
                </Select.Option>
              ))
            : null}
        </Select>
      </Form.Item>
    );
  };

  /*
   * Create DropdownWithServerData;
   *
   * Dropdown where data is dynamic data from server.
   * And Data will gotten from server by @dataSearchAction function.
   * @dataSearchAction function should have @searchString parameter.
   *
   * Searching will call @dataSearchAction function and will filtered by server part.
   * Or if @filterByChildData will be true than
   * searching will not call server method and will filter by children data;
   *
   * If @placeholder will be empty then value will be from label;
   * @mode - Set mode of Select. Can be 'single', 'tags', 'multiple'. Multiple is default value;
   * @maxTagCount - Max tag text length to show. Default value is 4;
   *
   * @data should have key and value properties. This name will use by default,
   * if @data have different properties than need use
   * @keyPropertyName, @valuePropertyName, @textPropertyName, @titlePropertyName
   * to determine the name of the properties;
   *
   * For siple setup just use only @keyPropertyName and @textPropertyName.
   * @valuePropertyName by default equals @keyPropertyName
   * and @titlePropertyName by default equals @textPropertyName
   *
   * @key - key of Select.Option
   * @value - Default to filter with this property
   * @text - title of Select after select this Option and title in dropdown
   */ // ----------------------------------------------------------------------
  const createDropdownWithServerData = () => {
    const key = keyPropertyName ? keyPropertyName : 'key';
    const value = valuePropertyName ? valuePropertyName : key;
    const text = textPropertyName ? textPropertyName : 'value';
    const title = titlePropertyName ? titlePropertyName : text;

    const filtering = filterByChildData
      ? {
          optionFilterProp: 'children',
          filterOption: (input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0,
        }
      : {
          onSearch: handleSearch,
          filterOption: false,
          searchValue: searchFieldValue,
        };

    return (
      <Form.Item name={[name]} label={label} className="filter-form-item" wrapperCol={wrapperCol} rules={rules} tooltip={tooltip}>
        <Select
          {...filtering}
          allowClear
          autoClearSearchValue={false}
          showSearch
          showArrow
          mode={mode ? mode : 'multiple'}
          loading={dataLoading}
          getPopupContainer={rootContainer}
          notFoundContent={renderNoDataOrSpinner(dataLoading)}
          maxTagCount={maxTagCount ?? 4}
          maxTagPlaceholder={getMaxTagPlaceholder}
          placeholder={searchFieldValue ? searchFieldValue : (placeholder ?? `Select ${label}`)}
          onDropdownVisibleChange={subscriptionOnDropdownOpen}
          onChange={onChange}
          disabled={disabled}
        >
          {data && data.length
            ? data.map((d) => (
                <Select.Option key={d[key]} value={d[value]} title={typeof title === 'function' ? title(d) : d[title]}>
                  {typeof text === 'function' ? text(d) : d[text]}
                </Select.Option>
              ))
            : null}
        </Select>
      </Form.Item>
    );
  };

  /*
   * Create RangePicker;
   *
   * Simple Range Picker.
   * Searching will not call server method and will filter by children data;
   *
   * If @placeholder will be empty then value will be "Date From:" and "Date To:";
   * @placeholder should be array of 2 string values;
   */ // ----------------------------------------------------------------------
  const createRangePicker = () => {
    return (
      <Form.Item name={[name]} label={label} className="filter-form-item" wrapperCol={wrapperCol} rules={rules} tooltip={tooltip}>
        <RangePicker
          allowEmpty={[true, true]}
          style={{ width: '100%' }}
          allowClear
          format={(date) => date.utc().format('MM/DD/YYYY')}
          getPopupContainer={rootContainer}
          placeholder={placeholder ?? ['Date From:', 'Date To:']}
          ranges={{
            Today: [moment().utc().startOf('day'), moment().utc().endOf('day')],
            'This Month': [moment().utc().startOf('month'), moment().utc().endOf('month')],
            'Last Month': [moment().add(-1, 'month').utc().startOf('month'), moment().add(-1, 'month').utc().endOf('month')],
          }}
          onChange={onChange}
          disabled={disabled}
        />
      </Form.Item>
    );
  };

  /*
   * Selecting filter type
   */ // ----------------------------------------------------------------------
  let filter;

  switch (filterType) {
    case filterTypes.dropdownYesNo:
      filter = createDropdownYesNo();
      break;
    case filterTypes.inputText:
      filter = createInput();
      break;
    case filterTypes.dropdownWithLocalData:
      filter = createDropdownWithLocalData();
      break;
    case filterTypes.dropdownWithServerData:
      filter = createDropdownWithServerData();
      break;
    case filterTypes.rangePicker:
      filter = createRangePicker();
      break;
  }

  /*
   * If @span not empty filter will wrapped into Col tag with @span value
   */ // ----------------------------------------------------------------------
  if (span && filter) {
    filter = <Col span={span}>{filter}</Col>;
  }

  return filter;
};

export default Filter;
